import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from '../hooks/useTranslation';

const Gallery = () => {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch published photos
  const fetchPhotos = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/photos?is_published=true&limit=200`);
      const data = await response.json();
      if (data.success) {
        setPhotos(data.photos);
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/photos/categories`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories.filter(cat => cat.published > 0));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    fetchPhotos();
    fetchCategories();
  }, [fetchPhotos]);

  // Filter photos
  const filteredPhotos = photos.filter(photo => {
    const matchesCategory = selectedCategory === 'All' || photo.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      photo.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      photo.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Open lightbox
  const openLightbox = async (photo, index) => {
    setLightboxPhoto(photo);
    setCurrentPhotoIndex(index);
    
    // Increment view count
    try {
      await fetch(`${BACKEND_URL}/api/photos/${photo.id}?increment_view=true`);
    } catch (error) {
      console.error('Error incrementing view:', error);
    }
  };

  // Close lightbox
  const closeLightbox = () => {
    setLightboxPhoto(null);
  };

  // Navigate lightbox
  const navigateLightbox = (direction) => {
    const newIndex = direction === 'next' 
      ? (currentPhotoIndex + 1) % filteredPhotos.length
      : (currentPhotoIndex - 1 + filteredPhotos.length) % filteredPhotos.length;
    
    setCurrentPhotoIndex(newIndex);
    setLightboxPhoto(filteredPhotos[newIndex]);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!lightboxPhoto) return;
      
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigateLightbox('prev');
      if (e.key === 'ArrowRight') navigateLightbox('next');
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [lightboxPhoto, currentPhotoIndex]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{t('gallery.pageTitle') || 'Photo Gallery - Kioo Radio 98.1FM'}</title>
        <meta name="description" content={t('gallery.pageDescription') || 'Browse our photo gallery showcasing events, programs, and community moments at Kioo Radio.'} />
      </Helmet>

      {/* Header */}
      <div className="bg-gradient-to-r from-kioo-primary to-kioo-secondary text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            📸 {t('gallery.title') || 'Photo Gallery'}
          </h1>
          <p className="text-xl text-green-100">
            {t('gallery.subtitle') || 'Capturing moments of faith, community, and impact'}
          </p>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Bar */}
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t('gallery.searchPlaceholder') || 'Search photos...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent"
                />
                <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('All')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedCategory === 'All'
                    ? 'bg-kioo-primary text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {t('gallery.all') || 'All'} ({photos.length})
              </button>
              
              {categories.map(cat => (
                <button
                  key={cat.category}
                  onClick={() => setSelectedCategory(cat.category)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedCategory === cat.category
                      ? 'bg-kioo-primary text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {cat.category} ({cat.published})
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Photo Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-kioo-primary"></div>
            <p className="mt-4 text-gray-600">{t('gallery.loading') || 'Loading photos...'}</p>
          </div>
        ) : filteredPhotos.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">📷</div>
            <p className="text-xl text-gray-600">
              {searchQuery || selectedCategory !== 'All' 
                ? (t('gallery.noResults') || 'No photos found. Try a different search or category.')
                : (t('gallery.noPhotos') || 'No photos available yet. Check back soon!')}
            </p>
          </div>
        ) : (
          <>
            {/* Results Count */}
            <div className="mb-6 text-gray-600">
              {t('gallery.showing') || 'Showing'} <span className="font-semibold">{filteredPhotos.length}</span> {filteredPhotos.length === 1 ? (t('gallery.photo') || 'photo') : (t('gallery.photos') || 'photos')}
            </div>

            {/* Masonry Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredPhotos.map((photo, index) => (
                <div
                  key={photo.id}
                  onClick={() => openLightbox(photo, index)}
                  className="relative group cursor-pointer overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  <img
                    src={`${BACKEND_URL}${photo.url}`}
                    alt={photo.description || photo.category}
                    className="w-full h-64 object-cover"
                    loading="lazy"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <p className="text-sm font-semibold mb-1">{photo.category}</p>
                      {photo.description && (
                        <p className="text-xs line-clamp-2">{photo.description}</p>
                      )}
                    </div>
                  </div>

                  {/* Category Badge */}
                  <div className="absolute top-2 left-2">
                    <span className="bg-white/90 text-gray-800 text-xs px-2 py-1 rounded-full font-medium">
                      {photo.category}
                    </span>
                  </div>

                  {/* Zoom Icon */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="bg-white/90 rounded-full p-2">
                      🔍
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxPhoto && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 transition z-50"
            title="Close (Esc)"
          >
            ×
          </button>

          {/* Previous Button */}
          {filteredPhotos.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateLightbox('prev');
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-4xl hover:text-gray-300 transition bg-black/50 rounded-full w-12 h-12 flex items-center justify-center z-50"
              title="Previous (←)"
            >
              ‹
            </button>
          )}

          {/* Next Button */}
          {filteredPhotos.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateLightbox('next');
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-4xl hover:text-gray-300 transition bg-black/50 rounded-full w-12 h-12 flex items-center justify-center z-50"
              title="Next (→)"
            >
              ›
            </button>
          )}

          {/* Image Container */}
          <div 
            className="max-w-6xl max-h-[90vh] w-full flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image */}
            <div className="flex-1 flex items-center justify-center mb-4">
              <img
                src={`${BACKEND_URL}${lightboxPhoto.url}`}
                alt={lightboxPhoto.description || lightboxPhoto.category}
                className="max-w-full max-h-[calc(90vh-120px)] object-contain rounded-lg"
              />
            </div>

            {/* Info Bar */}
            <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 text-white">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                      {lightboxPhoto.category}
                    </span>
                    <span className="text-sm text-gray-300">
                      {currentPhotoIndex + 1} / {filteredPhotos.length}
                    </span>
                  </div>
                  {lightboxPhoto.description && (
                    <p className="text-sm text-gray-200">{lightboxPhoto.description}</p>
                  )}
                </div>
                
                {/* Download Button */}
                <a
                  href={`${BACKEND_URL}${lightboxPhoto.url}`}
                  download
                  onClick={(e) => e.stopPropagation()}
                  className="ml-4 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-sm font-medium transition"
                >
                  ⬇️ {t('gallery.download') || 'Download'}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
